import { Button } from '@/components/ui/button';
import { MapPin } from 'lucide-react';
import type { GolfCourseApiCourse, GolfCourseApiTeeSet } from '@/types/golfCourseApi';

interface CoursePreviewStepProps {
  course: GolfCourseApiCourse;
  onContinue: () => void;
  onBack: () => void;
}

/** Flatten the { male: [...], female: [...] } tees dict into a labeled list */
function flattenTees(tees: Record<string, GolfCourseApiTeeSet[]>): { tee: GolfCourseApiTeeSet; gender: string }[] {
  const result: { tee: GolfCourseApiTeeSet; gender: string }[] = [];
  for (const [gender, teeList] of Object.entries(tees)) {
    if (!Array.isArray(teeList)) continue;
    for (const tee of teeList) {
      result.push({ tee, gender });
    }
  }
  return result;
}

const CoursePreviewStep = ({ course, onContinue, onBack }: CoursePreviewStepProps) => {
  const location = [course.location?.city, course.location?.state]
    .filter(Boolean)
    .join(', ');

  const allTees = flattenTees(course.tees || {});

  // Derive holes count and par from the first tee set
  const firstTee = allTees[0]?.tee;
  const holesCount = firstTee?.number_of_holes || firstTee?.holes?.length || 18;
  const par = firstTee?.par_total || '—';

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-lg">
          {course.course_name || course.club_name}
        </h3>
        {location && (
          <div className="flex items-center text-sm text-muted-foreground mt-1">
            <MapPin className="h-3.5 w-3.5 mr-1" />
            {location}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="bg-muted rounded-md p-3">
          <div className="text-muted-foreground">Holes</div>
          <div className="font-semibold text-lg">{holesCount}</div>
        </div>
        <div className="bg-muted rounded-md p-3">
          <div className="text-muted-foreground">Par</div>
          <div className="font-semibold text-lg">{par}</div>
        </div>
      </div>

      {allTees.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Available Tees</h4>
          <div className="space-y-1.5">
            {allTees.map(({ tee, gender }, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-muted rounded-md px-3 py-1.5">
                <span>
                  {tee.tee_name}
                  <span className="text-muted-foreground ml-1 text-xs capitalize">
                    ({gender})
                  </span>
                </span>
                <span className="text-muted-foreground">
                  {tee.total_yards ? `${tee.total_yards.toLocaleString()} yds` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Back
        </Button>
        <Button onClick={onContinue} className="flex-1 bg-forest hover:bg-forest/90 text-white">
          Continue
        </Button>
      </div>
    </div>
  );
};

export default CoursePreviewStep;
